import React from 'react';
import { useTranslation } from 'react-i18next';
import { HardDrive, X } from 'lucide-react';
import { StorageInfo } from '../hooks/useStorage';
import BodyPortal from './BodyPortal';

interface StorageExceededDialogProps {
  onClose: () => void;
  storageInfo?: StorageInfo | null;
}

const StorageExceededDialog: React.FC<StorageExceededDialogProps> = ({ onClose, storageInfo }) => {
  const { t } = useTranslation();

  const usedPct = storageInfo
    ? Math.min(100, Math.round((storageInfo.usedStorage / storageInfo.providedStorage) * 100))
    : 100;

  const fmt = (gb: number) =>
    gb < 1 ? `${Math.round(gb * 1024)} MB` : `${gb.toFixed(1)} GB`;

  return (
    <BodyPortal>
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        background: 'rgba(0,0,0,0.5)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--card, #fff)',
          color: 'var(--cardForeground, #111827)',
          borderRadius: 16,
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
          width: 340,
          textAlign: 'center',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('cancel', 'Cancel')}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--textSecondary, #6b7280)',
            padding: 4,
          }}
        >
          <X size={18} aria-hidden="true" />
        </button>

        {/* Icon */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'var(--attendanceAbsentBg, #fef2f2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <HardDrive size={32} style={{ color: 'var(--error, #ef4444)' }} aria-hidden="true" />
        </div>

        <div>
          <p style={{ fontWeight: 700, fontSize: 17, margin: '0 0 6px' }}>
            {t('storage.exceeded_title', 'Storage limit reached')}
          </p>
          <p style={{ fontSize: 13, color: 'var(--textSecondary, #6b7280)', margin: 0, lineHeight: 1.5 }}>
            {t('storage.exceeded_desc', 'You have used all available storage. Contact your administrator to request more space.')}
          </p>
        </div>

        {/* Usage bar */}
        {storageInfo && (
          <div style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: 'var(--textSecondary, #6b7280)',
                marginBottom: 6,
              }}
            >
              <span>{t('storage.used', 'Used')}: {fmt(storageInfo.usedStorage)}</span>
              <span>{t('storage.total', 'Total')}: {fmt(storageInfo.providedStorage)}</span>
            </div>
            <div
              style={{
                height: 8,
                borderRadius: 4,
                background: 'var(--controlsBg, #f3f4f6)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${usedPct}%`,
                  background: 'var(--error, #ef4444)',
                  borderRadius: 4,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <p
              style={{
                textAlign: 'right',
                fontSize: 11,
                color: 'var(--error, #ef4444)',
                margin: '4px 0 0',
              }}
            >
              {usedPct}% {t('storage.used', 'used')}
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%',
            padding: '10px 0',
            borderRadius: 8,
            border: 'none',
            background: 'var(--primary, #6366f1)',
            color: 'var(--button-primary-foreground, #fff)',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          {t('storage.understood', 'Understood')}
        </button>
      </div>
    </div>
    </BodyPortal>
  );
};

export default StorageExceededDialog;
